import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, take, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { EntityTypes } from 'constants/entities';
import { IAttemptAction } from 'types/action/player';
import { restoreState, updateState } from 'core/actions/product/state';
import { selectOfflineId } from 'core/selectors/admin/offlineId';
import { RootState } from 'types';
import { selectActionFirst } from 'core/selectors/product/action/actionFirst';
import { selectModuleDAO } from 'core/selectors/product/module';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { Mode } from 'core/slices/session';
import {
  DialogueRegisterState,
  IActionRegister,
  IDialogueRegister,
  IPreview,
  IRegisterAction,
  RegisterResult,
  actionsComplete,
  next,
  preview,
  registerAction,
  registerActionResult,
  registerAttempt,
  registerDialogue,
  updateVerification,
} from 'core/slices/register';
import { startAction } from '../module/start';
import { CourseNodeRole, CourseNodeType, ICourseNodeDAO, ICourseNodeEntity } from 'types/domain/course';
import { selectCourseNode, selectDefaultCourseNode } from 'core/selectors/admin/courseNode';
import {
  processDialogue,
  processGroup,
  processModule,
  processNextNode,
  processNodeOut,
  processSystem,
  processVariable,
} from './processor';
import { PreviewType, clearPreview, updatePreview } from 'core/slices/preview';
import { ISelectNode } from 'core/slices/flow';
import { generateOfflineId } from 'core/slices/offline';
import { IVerificationEntity } from 'types/domain/verification';
import { createRequest, getRequest } from 'core/services/admin';
import { loadModule } from '../module/load';
import { moduleRequest } from 'core/services/product';
import { selectAction, selectActionDAO } from 'core/selectors/product/action/action';
import { selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { selectPreviewType } from 'core/selectors/home/preview';
import { IAction, IActionDAO } from 'types/domain/action';
import { Handles } from 'constants/handles';
import { queueItem } from 'core/slices/queue';

function* previewActionSaga(action: PayloadAction<IPreview>) {
  const offlineId = yield select(selectOfflineId);
  yield put(yield call(generateOfflineId, null));
  yield put(yield call(updateState, { type: EntityTypes.COURSE_PROGRESS, id: offlineId }));

  if (action.payload.moduleId) yield previewModule(action);
  else {
    const defaultNode = yield select(selectDefaultCourseNode);
    const courseNodeId = yield select((state: RootState) => state.state[EntityTypes.COURSE_NODE]);

    yield previewCourseNode(action.payload.courseNodeId || courseNodeId || defaultNode?.id);
  }
}

function* previewModule(action: PayloadAction<IPreview>) {
  const mode: Mode = yield select(selectSessionMode);
  const offlineId = yield select(selectOfflineId);
  const courseProgressId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);

  let module = yield select((state: RootState) => selectModuleDAO(state, action.payload.moduleId));
  const selectedNode: ISelectNode = yield select((state: RootState) => state.flow.selectedNode);
  const selectedActionId = selectedNode && selectedNode.type === EntityTypes.ACTION ? selectedNode.id : null;

  let loading = yield loadModule(action.payload.moduleId);

  while (loading) {
    const modLoad = yield take([moduleRequest.fulfilled, getRequest.fulfilled]);
    loading = modLoad.payload.id !== action.payload.moduleId;
    module = yield select((state: RootState) => selectModuleDAO(state, action.payload.moduleId));
  }

  yield all([
    put(
      yield call(registerAttempt, {
        offlineId: offlineId,
        sessionId: offlineId,
        sessionAttemptId: offlineId,
        courseProgressId,
        moduleId: action.payload.moduleId,
        type: module.type,
        isPreview: true,
      } as IAttemptAction),
    ),
    put(
      yield call(restoreState, [
        {
          type: EntityTypes.SESSION_ATTEMPT,
          id: offlineId,
        },
      ]),
    ),
    put(
      yield call(updatePreview, {
        type: EntityTypes.MODULE,
        id: action.payload.moduleId,
        data: { courseProgressId },
      }),
    ),
  ]);

  yield put(
    yield call(queueItem, {
      courseNodeId: action.payload.courseNodeId,
      moduleId: action.payload.moduleId,
      sessionAttemptId: offlineId,
      courseProgressId,
    }),
  );

  // Resolve which action to start the preview on. Prefer an explicitly requested
  // action (e.g. toggling to preview while editing a specific action), then the
  // action selected in the flow editor. A stale action left over from a
  // previously-edited module must be ignored, otherwise the previous action would
  // show when moving from module to module.
  let targetActionId = action.payload.actionId ?? selectedActionId ?? null;

  if (targetActionId != null) {
    let actionDAO = yield select((state: RootState) =>
      //@ts-ignore
      selectAction(state, targetActionId),
    );

    // Wait for a freshly-created (unsaved) action to resolve to its real id.
    let saving = actionDAO && actionDAO.id === actionDAO.offlineId;
    while (saving) {
      const saved = yield take(createRequest.fulfilled);
      if (actionDAO.offlineId === saved.payload.offlineId) {
        saving = false;
        actionDAO = yield select((state: RootState) =>
          //@ts-ignore
          saved.payload.id ? selectAction(state, saved.payload.id) : null,
        );
      }
    }

    // Only honour the action if it belongs to the module being previewed
    // (validate against the payload module, not the racy state slice).
    targetActionId = actionDAO && actionDAO.moduleId === action.payload.moduleId ? actionDAO.id : null;
  }

  if (targetActionId == null) {
    const firstAction = yield select(state => selectActionFirst(state, action.payload.moduleId));
    targetActionId = firstAction?.id ?? module.actionId;
  }

  // The waits above (module load, unsaved action) mean the module on screen may have
  // moved on since this run started. Selecting our action now would drop the previous
  // module's slide into the new module.
  const currentModuleId = yield select((state: RootState) => state.state[EntityTypes.MODULE]);
  if (currentModuleId && currentModuleId !== action.payload.moduleId) return;

  // startAction sets the current action AND positions the feed index on it, so the
  // preview opens on the action being edited with a back-navigable synthetic history
  // behind it, rather than restarting from the beginning of the module.
  yield startAction(targetActionId);
}

export function* previewCourseNode(courseNodeId: number, targetHandleId?: number, previousNodeId?: number) {
  let courseNode: ICourseNodeDAO = yield select((state: RootState) => selectCourseNode(state, courseNodeId));

  while (!courseNode) {
    yield take(getRequest.fulfilled);
    courseNode = yield select((state: RootState) => selectCourseNode(state, courseNodeId));
  }

  if (!courseNode || courseNode?.status === -1) return;

  yield put(
    yield call(updateState, { type: EntityTypes.COURSE_NODE, id: courseNode.id, culprit: 'previewCourseNode' }),
  );

  switch (courseNode.type) {
    case CourseNodeType.START:
      return yield processSystem(courseNode);
    case CourseNodeType.ASSIGN:
      switch (courseNode.entityType) {
        case CourseNodeRole.VARIABLE_SINGLE:
        case CourseNodeRole.VARIABLE:
          return yield processVariable(courseNode, targetHandleId, previousNodeId);
        case CourseNodeRole.MODULE:
          return yield processModule(courseNode);
      }
      break;
    case CourseNodeType.END:
    case CourseNodeType.RESPONSE:
    case CourseNodeType.REQUEST:
    case CourseNodeType.NOTIFICATION:
      return yield processDialogue(courseNode);
    case CourseNodeType.MENU: {
      //yield put(yield call(clearHistory));
      return yield processDialogue(courseNode);
    }
    case CourseNodeType.LOGIC:
      switch (courseNode.entityType) {
        case CourseNodeRole.VARIABLE_SINGLE:
        case CourseNodeRole.VARIABLE:
          return yield processVariable(courseNode, targetHandleId, previousNodeId);
        case CourseNodeRole.GROUP:
          return yield processGroup(courseNode, targetHandleId);
      }
  }
}

function* updateVerificationSaga(action: PayloadAction<any>) {
  const mode: Mode = yield select(selectSessionMode);
  const verificationEntities = yield select(
    (state: RootState) => state.entity.present[EntityTypes.VERIFICATION_ENTITY],
  );
  if (mode === Mode.PREVIEW && action.payload.entities) {
    yield all(
      Object.values(action.payload.entities).map((entity: any) => {
        const verifEntity: IVerificationEntity = verificationEntities[entity.verificationEntityId];
        return processNodeOut(verifEntity.courseNodeId, verifEntity.id);
      }),
    );
    yield put(yield call(next, {}));
  }
}

function* actionsCompleteSaga(action: PayloadAction<IRegisterAction>) {
  const feed = yield select(selectActionFeed);
  const mode: Mode = yield select(selectSessionMode);
  const previewType: PreviewType = yield select(selectPreviewType);

  if (action.payload && action.payload.actionId) {
    //@ts-ignore
    const actionDAO: IActionDAO = yield select((state: RootState) => selectActionDAO(state, action.payload.actionId));

    if (action.payload.actionEntityId) {
      const modOut = actionDAO.outs[action.payload.actionEntityId];
      if (modOut) return;
    }

    switch (action.payload.result) {
      case RegisterResult.DEFAULT:
        if (actionDAO.outs[Handles.DEFAULT]) return;
        break;
    }
  }

  if (
    previewType === PreviewType.COURSE &&
    mode === Mode.PREVIEW &&
    [feed.feed.length, feed.feed.length - 1].includes(feed.index)
  ) {
    const courseNode: ICourseNodeDAO = yield select(selectCourseNode);

    if (!courseNode) return;

    if (courseNode.variableId || courseNode.moduleId) {
      const success = yield processVariable(courseNode, null, null);
      if (!success) yield processNextNode();
    } else yield processNextNode();
  }
}

function* registerDialogueSaga(action: PayloadAction<IDialogueRegister>) {
  const mode: Mode = yield select(selectSessionMode);

  if (mode !== Mode.PREVIEW) return;

  const courseNode: ICourseNodeDAO = yield select((state: RootState) =>
    selectCourseNode(state, action.payload.courseNodeId),
  );

  if (courseNode.type === CourseNodeType.MENU) {
    yield previewCourseNode(action.payload.targetCourseNodeId);
    yield put(yield call(next, {}));
    return;
  }

  if (courseNode.entityType === CourseNodeRole.GROUP && action.payload.courseNodeEntityId) {
    const entity: ICourseNodeEntity = courseNode.entities.find(ent => ent.id === action.payload.courseNodeEntityId);

    if (entity.groupId) yield put(yield call(updatePreview, { type: EntityTypes.GROUP, id: entity.groupId }));

    if (entity && entity.outs.length)
      yield processNodeOut(action.payload.courseNodeId, action.payload.courseNodeEntityId);
  }

  if (action.payload.state === DialogueRegisterState.SUBMITTED) {
    yield processNextNode(courseNode.id);
    yield put(yield call(next, {}));
  }
}

function* clearPreviewSaga() {
  const previewType: PreviewType = yield select((state: RootState) => state.preview?.type);

  yield put(
    yield call(restoreState, [
      { type: EntityTypes.COURSE_PROGRESS, id: null },
      { type: EntityTypes.COURSE_NODE, id: null },
      { type: EntityTypes.SESSION_ATTEMPT, id: null },
      { type: EntityTypes.ACTION, id: null },
      { type: EntityTypes.SCENE, id: null },
      { type: EntityTypes.VERIFICATION_REQUEST, id: null },
      { ...(previewType === PreviewType.COURSE ? { type: EntityTypes.MODULE, id: null } : null) },
    ]),
  );
}
function* registerActionSaga(action: PayloadAction<IActionRegister>) {
  const a: IAction = yield select(
    (state: RootState) => state.entity.present[EntityTypes.ACTION][action.payload.actionId],
  );
  const variableId = yield select(
    (state: RootState) => state.entity.present[EntityTypes.MODULE][a.moduleId]?.variableId,
  );

  const previewVariable = yield select((state: RootState) => state.preview?.variables[variableId]);

  if (!previewVariable)
    yield put(
      yield call(updatePreview, {
        type: EntityTypes.VARIABLE,
        id: variableId,
        data: {},
      }),
    );
}

function* watchPreview() {
  // takeLatest, not takeEvery: previewing blocks — on the module load, and on any
  // unsaved action resolving to a real id — so a run for the module you just left
  // is still in flight when the next one starts, and finishes by selecting *its*
  // action. That is how the previous module's slide came back after a switch.
  // Only the newest preview may drive the selection.
  yield takeLatest(preview, previewActionSaga);
}

function* watchActionsComplete() {
  yield takeEvery([registerActionResult, actionsComplete], actionsCompleteSaga);
}

function* watchUpdateVerification() {
  yield takeEvery(updateVerification, updateVerificationSaga);
}

function* watchRegisterAction() {
  yield takeEvery(registerAction, registerActionSaga);
}

function* watchRegisterDialogue() {
  yield takeEvery(registerDialogue, registerDialogueSaga);
}

function* watchClearPreview() {
  yield takeEvery(clearPreview, clearPreviewSaga);
}

export function* previewSaga() {
  yield all([
    fork(watchPreview),
    fork(watchActionsComplete),
    fork(watchUpdateVerification),
    fork(watchRegisterDialogue),
    fork(watchRegisterAction),
    fork(watchClearPreview),
  ]);
}
